import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, Box, Button} from 'views/components';
import {Translate, translate, SecureFileGrid} from 'views/module';

import moment from 'moment';

const CertificateView = (props) => {
  const {bioQuestions, setIsComplete} = props;

  // track which certificates have attachments visible
  const [openIndexes, setOpenIndexes] = React.useState([]);

  const certificates = React.useMemo(() => {
    return bioQuestions?.certificates || [];
  }, [bioQuestions]);

  // Check to see if any cert has been complete
  React.useEffect(() => {
    const hasCompleteCertification = certificates.length > 0;
    setIsComplete(hasCompleteCertification);
  }, [certificates, setIsComplete]);


  const handleToggleAttachments = React.useCallback((index) => {
    setOpenIndexes((prev) =>
      prev.includes(index) ?
        prev.filter((i) => i !== index) :
        [...prev, index],
    );
  }, []);

  if (!certificates.length) {
    return (
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          <Translate expression="caregiver.CaregiverBiography.Certificate.EmptyState" />
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} alignItems="flex-start" sx={{width: '100%'}}>
      {
        certificates.map((cert, index) => {
          const {type, issuingOrganization, dateObtained, city, attachments = []} = cert;

          let dateObtainedString = null;
          const date = moment(dateObtained);
          if (date.isValid()) {
            dateObtainedString = date.format('YYYY');
          }
          let certificateType = translate(`caregiver.CaregiverBiography.Certificate.AvailableCertifications.${type}`);
          if (certificateType === 'Missing translation') certificateType = type;
          const details = [
            certificateType,
            issuingOrganization,
            dateObtainedString,
            city,
          ].filter(Boolean).join(' \u2022 ');

          const showAttachments = openIndexes.includes(index);

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #0288d1',
                borderRadius: '12px',
                backgroundColor: '#e1f5fe',
                paddingY: 1,
                paddingX: 1.5,
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <Stack sx={{width: '100%'}}>
                <Typography
                  variant="body2"
                  sx={{color: '#0288d1', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis'}}
                >
                  {details}
                </Typography>
                {
                  attachments.length > 0 &&
                  <Stack spacing={0.5} sx={{width: '100%'}}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => handleToggleAttachments(index)}
                      sx={{
                        alignSelf: 'flex-start',
                        padding: 0,
                        textTransform: 'none',
                        minHeight: 0,
                        fontSize: '0.8rem',
                        color: '#0288d1',
                        fontWeight: 500,
                      }}
                    >
                      {showAttachments ?
                        translate('caregiver.CaregiverBiography.Certificate.HideAttachments') :
                        translate('caregiver.CaregiverBiography.Certificate.ShowAttachments')}
                    </Button>
                    {
                      showAttachments &&
                      <>
                        <Typography variant="caption" sx={{color: '#0288d1', fontWeight: 500}}>
                          <Translate expression="caregiver.CaregiverBiography.Certificate.Attachments" />
                        </Typography>
                        <SecureFileGrid files={attachments} />
                      </>
                    }
                  </Stack>
                }
              </Stack>
            </Box>
          );
        })
      }
    </Stack>
  );
};

CertificateView.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  setIsComplete: PropTypes.func.isRequired,
};

export default React.memo(CertificateView);
